/**
 * Aanwezigheidskalender — vaste legenda (Epic 4, Story 4.2, AC 1)
 *
 * Toont alle statuscategorieën: werk (kantoor/thuis-icoon), leeg
 * (niet ingeroosterd), verlof (één uniform "Vrij"-item, pastel — zie Story
 * aanwezigheid-verlof-vrij 1.1 hieronder), feestdag (kleur), weekend
 * (grijs), BHV-icoon.
 *
 * Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4, AC 6): het
 * "in-afwachting"-legenda-item (teal-arcering) is verwijderd — een pending
 * verlofaanvraag toont niet meer apart in de kalender.
 *
 * Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-2/CAP-3): het
 * "Structureel vrij (rooster-patroon)"-arceringsitem is vervangen door een
 * leeg swatch + "(leeg) = niet ingeroosterd" (AC 5); de verloftype-swatches
 * gebruiken nu dezelfde `pastelTint`/`readableInk`-behandeling als de cel
 * zelf (consistent met de cel, geen los-verzadigde kleur meer in de
 * legenda die niet meer overeenkomt met de pastel-cel).
 *
 * Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-2, AC 3): de
 * per-verloftype-swatches (één item per aanwezig verloftype) zijn
 * VERVANGEN door één uniform "Vrij"-legenda-item — behoudt de
 * `presentLeaveTypes`-propsignatuur (call-site `AanwezigheidPage.tsx:137`
 * ongewijzigd), collapset intern naar één item zodra
 * `presentLeaveTypes.length > 0`.
 */

import {
  pastelTint,
  readableInk,
  LEAVE_PASTEL_AMOUNT,
  VRIJ_COLOR,
  HOLIDAY_BG,
  HOLIDAY_BORDER,
  HOLIDAY_COLOR,
  ZIEK_STRIPE_LIGHT,
  ZIEK_STRIPE_DARK,
  ZIEK_TEXT,
  KANTOOR_BG,
  KANTOOR_ACCENT,
  THUIS_BG,
  THUIS_ACCENT,
} from './aanwezigheidVisuals';
import { EhboBadge } from './BhvBadge';

interface AanwezigheidLegendProps {
  /**
   * Presence-signal for "is er goedgekeurd verlof in de huidige view?" — na
   * de Story 1.1-consolidatie (aanwezigheid-verlof-vrij) doet alleen
   * `.length > 0` ertoe, niet de concrete waarden. Bevat de echte
   * verloftype-nummers wanneer de kijker ze mag zien, of een presence-only
   * placeholder (`-1`) wanneer er wél goedgekeurd verlof is maar het
   * concrete type niet zichtbaar is (peer-view, AD-6 privacy) — zie
   * `AanwezigheidPage.tsx`.
   */
  presentLeaveTypes: number[];
}

export function AanwezigheidLegend({ presentLeaveTypes }: AanwezigheidLegendProps) {
  return (
    <div
      data-testid="aanwezigheid-legend"
      className="flex flex-wrap items-center gap-4 px-4 py-3 bg-white border-t border-[#E2E8F0] text-xs text-[#1E293B]"
    >
      {/* Epic ux-aanwezigheidscel-duur, Story 1.2 (CAP-2/AC 5): twee
          "Werkt"-swatches i.p.v. de oude ene groene — de legenda voedt zich
          uit dezelfde aanwezigheidVisuals-constanten als de cel (geen
          tweede kleurtabel, tech-spec sectie "Legenda-wijzigingen"). */}
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: KANTOOR_BG, boxShadow: `inset 2px 0 0 ${KANTOOR_ACCENT}` }}
        />
        <span>Kantoor</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: THUIS_BG, boxShadow: `inset 2px 0 0 ${THUIS_ACCENT}` }}
        />
        <span>Thuis</span>
      </div>

      {/* Epic aanwezigheidscel-iteratie2, Story 1.2 (CAP-2, AC 5): VERVANGT
          het oude "Structureel vrij (rooster-patroon)"-arceringsitem — een
          leeg wit swatch, zodat een blanco vak in het grid verklaard
          blijft in plaats van helemaal geschrapt te worden. */}
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm border border-[#E2E8F0] bg-white" />
        <span>(leeg) = niet ingeroosterd</span>
      </div>

      {/* Epic aanwezigheid-verlof-vrij, Story 1.1 (CAP-2, AC 3): één
          "Vrij"-item op de uniforme VRIJ_COLOR-pastel — vervangt de oude
          per-verloftype-swatches (`presentLeaveTypes.map(...)`). Getoond
          zodra er goedgekeurd verlof in de view is
          (`presentLeaveTypes.length > 0`); welke specifieke types dat zijn
          is voor de legenda niet langer relevant. */}
      {presentLeaveTypes.length > 0 && (
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-sm border border-[#E2E8F0]"
            style={{ backgroundColor: pastelTint(VRIJ_COLOR, LEAVE_PASTEL_AMOUNT) }}
          />
          <span style={{ color: readableInk(VRIJ_COLOR) }}>Vrij</span>
        </div>
      )}

      {/* Story 1.2 (CAP-2/AC 5, Open Decision 2): gedempte arcering i.p.v.
          de oude massief-navy swatch — dezelfde stijl-familie als de
          herstylede ziek-cel.
          Epic aanwezigheidscel-iteratie2, Story 1.3 (CAP-5, AC 2): het
          medisch-kruis-icoon is VERWIJDERD uit dit legenda-item — alleen
          de gedempte arcering + het "Ziek"-tekstlabel, zoals de cel. */}
      <div className="flex items-center gap-1.5">
        <span
          className="inline-block w-3 h-3 rounded-sm border border-[#E2E8F0]"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, ${ZIEK_STRIPE_LIGHT}, ${ZIEK_STRIPE_LIGHT} 2px, ${ZIEK_STRIPE_DARK} 2px, ${ZIEK_STRIPE_DARK} 4px)`,
          }}
        />
        <span style={{ color: ZIEK_TEXT }}>Ziek</span>
      </div>

      {/* Epic aanwezigheidscel-iteratie2, Story 1.1 (CAP-4, AC 6): het
          "Verlof — in afwachting (arcering)"-legenda-item is VERWIJDERD —
          een pending verlofaanvraag beïnvloedt de kalender niet meer, dus
          er valt niets meer te verklaren voor deze categorie. */}

      {/* Story 1.2 (CAP-3/AC 13): duur-balk als swatch. Bewust GEEN los
          "Halve dag"-item — een halve dag is merge-uitgedrukt (één gevulde
          helft), geen eigen legenda-categorie (review-bevinding 8). */}
      <div className="flex items-center gap-1.5">
        <span className="relative inline-block w-4 h-3 rounded-sm bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden">
          <span className="absolute bottom-0 left-0 h-0.5 w-3/4" style={{ backgroundColor: KANTOOR_ACCENT }} />
        </span>
        <span>Verkorte dag (eerder klaar)</span>
      </div>

      {/* Epic kort-verlof-duur, Story 1.1 (CAP-2, UX-spec "Legenda-
          toevoeging"): een werk-swatch met een klein violet staartje —
          een kort verlof (bv. tandartsbezoek) houdt de cel "werkt", met
          een gestapelde werk+vrij-balk + `Xu vrij`-badge als annotatie
          (zie AanwezigheidCell).
          Epic verlof-correctie/kort-verlof-met-tijd, Story 1.5 (AC-6/AC-12,
          UX-spec regels 150-151): het label is verbreed van "Kort verlof
          (uren)" naar "Kort verlof" + een tweede, gedempte regel "met tijd
          als die bekend is" — dekt nu zowel het bestaande duur-alleen-pad
          als het nieuwe tijdvak-pad. */}
      <div className="flex items-center gap-1.5">
        <span className="relative inline-block w-4 h-3 rounded-sm bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden">
          <span className="absolute bottom-0 left-0 h-0.5 w-1/2" style={{ backgroundColor: KANTOOR_ACCENT }} />
          <span className="absolute bottom-0 right-0 h-0.5 w-1/2" style={{ backgroundColor: VRIJ_COLOR }} />
        </span>
        <span className="flex flex-col leading-tight">
          <span>Kort verlof</span>
          <span className="text-[10px] text-gray-500">met tijd als die bekend is</span>
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: HOLIDAY_BG, border: `1px solid ${HOLIDAY_BORDER}` }} />
        <span style={{ color: HOLIDAY_COLOR }}>Feestdag</span>
      </div>

      <div className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm bg-[#F3F4F6] border border-[#E2E8F0]" />
        <span className="text-gray-500">Weekend</span>
      </div>

      {/* Epic ux-aanwezigheidscel-duur, Story 1.3 (CAP-4, Open Decision
          1): "BHV (alleen-weergave)" is VERVANGEN door "BHV — aanwezig",
          met hetzelfde rood-wit EHBO-teken als op de cel — de tekst
          benadrukt expliciet dat dit nu een dekkingssignaal is (alleen
          zichtbaar bij daadwerkelijke aanwezigheid), niet louter een
          identiteits-icoon. */}
      <div className="flex items-center gap-1.5">
        <span className="relative inline-block w-5 h-5">
          <EhboBadge />
        </span>
        <span className="text-gray-500">BHV — aanwezig (alleen bij daadwerkelijke aanwezigheid)</span>
      </div>
    </div>
  );
}
